import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, FileText, CheckCircle, XCircle, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminTechnicalDocs = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: 'Woodward Kartları',
    file_url: '',
    file_name: '',
    content: '',
    status: 'active',
  });

  const categories = [
    'Woodward Kartları',
    'Senkron Panolar',
    'Arıza Kodları',
    'Genel Teknik',
    'Enerji Yönetim Sistemleri',
    'PLC ve SCADA',
    'Diğer'
  ];

  useEffect(() => {
    fetchDocs();
  }, []);

  const fetchDocs = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/technical-docs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocs(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Dokümanlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const allowedExtensions = ['.pdf', '.docx', '.txt'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error('Sadece PDF, DOCX veya TXT dosyaları yüklenebilir');
      return;
    }

    // Check file size (15MB limit)
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Dosya boyutu 15MB\'dan küçük olmalıdır');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formDataToSend = new FormData();
      formDataToSend.append('file', file);

      const response = await axios.post(`${API}/upload-document`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update form with extracted content
      setFormData({
        ...formData,
        title: formData.title || file.name.replace(/\.[^/.]+$/, ''),
        file_url: response.data.url,
        file_name: response.data.filename,
        content: response.data.content,
      });

      const pageInfo = response.data.pages_extracted > 0 
        ? ` (${response.data.pages_extracted} sayfa)` 
        : '';
      const charInfo = response.data.content_length 
        ? ` - ${response.data.content_length.toLocaleString()} karakter` 
        : '';
      
      toast.success(`Dosya yüklendi ve içerik çıkarıldı!${pageInfo}${charInfo}`);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Dosya yüklenirken hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const handleFileUrlExtract = async () => {
    if (!formData.file_url) {
      toast.error('Lütfen bir dosya URL\'si girin');
      return;
    }

    setExtracting(true);
    try {
      // Extract file name from URL
      const fileName = formData.file_url.split('/').pop() || 'document';
      
      // Simulate content extraction
      // In production, you would call an actual extraction service
      const mockContent = `Doküman içeriği ${fileName} dosyasından çıkarıldı.\n\nBu alan gerçek uygulamada PDF/DOCX/TXT içeriğini içerecek.`;
      
      setFormData({
        ...formData,
        file_name: fileName,
        content: mockContent,
      });
      
      toast.success('Dosya içeriği çıkarıldı. İçeriği düzenleyebilirsiniz.');
    } catch (error) {
      console.error('Error extracting content:', error);
      toast.error('Dosya içeriği çıkarılırken hata oluştu');
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.content.trim()) {
      toast.error('Lütfen doküman içeriği girin');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/technical-docs`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success('Doküman başarıyla eklendi');
      setShowDialog(false);
      resetForm();
      fetchDocs();
    } catch (error) {
      console.error('Error creating document:', error);
      toast.error('Doküman eklenirken hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu dokümanı silmek istediğinizden emin misiniz?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/technical-docs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success('Doküman silindi');
      fetchDocs();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Doküman silinirken hata oluştu');
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API}/technical-docs/${id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Doküman ${newStatus === 'active' ? 'aktif' : 'pasif'} edildi`);
      fetchDocs();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Durum güncellenirken hata oluştu');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: 'Woodward Kartları',
      file_url: '',
      file_name: '',
      content: '',
      status: 'active',
    });
    setUploading(false);
    setExtracting(false);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">AI Bilgi Bankası</h1>
            <p className="text-gray-600 mt-2">
              Teknik dokümanlar yükleyin ve AI chatbot'un bu bilgilerle müşterilere yardım etmesini sağlayın
            </p>
          </div>
          <Button onClick={() => setShowDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Yeni Doküman
          </Button>
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">💡 Nasıl Çalışır?</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Teknik dokümanlarınızı (PDF, DOCX, TXT) yükleyin</li>
            <li>• AI chatbot bu dokümanları okuyarak teknisyenlere adım adım yardım eder</li>
            <li>• Woodward kartları, senkron panolar ve arıza çözümleri için özel destek</li>
            <li>• Aktif dokümanlar chatbot tarafından kullanılır</li>
          </ul>
        </div>

        {/* Documents Table */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          </div>
        ) : docs.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Henüz doküman eklenmemiş</p>
            <Button onClick={() => setShowDialog(true)} className="mt-4">
              İlk Dokümanı Ekle
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Başlık</TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead>Dosya</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-medium">{doc.title}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {doc.category}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">{doc.file_name}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => toggleStatus(doc.id, doc.status)}
                        className="flex items-center gap-1"
                      >
                        {doc.status === 'active' ? (
                          <span className="flex items-center text-green-600">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Aktif
                          </span>
                        ) : (
                          <span className="flex items-center text-gray-400">
                            <XCircle className="w-4 h-4 mr-1" />
                            Pasif
                          </span>
                        )}
                      </button>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {new Date(doc.created_at).toLocaleDateString('tr-TR')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(doc.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Add Document Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Yeni Teknik Doküman Ekle</DialogTitle>
              <DialogDescription>
                Teknik doküman bilgilerini girin. AI chatbot bu bilgileri kullanarak yardım edecek.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Doküman Başlığı *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Örn: Woodward Senkron Kartı Kurulum Kılavuzu"
                  required
                />
              </div>

              <div>
                <Label htmlFor="category">Kategori *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* File Upload */}
              <div>
                <Label htmlFor="file-upload">Dosya Yükle (PDF, DOCX, TXT)</Label>
                <div className="mt-2">
                  <label
                    htmlFor="file-upload"
                    className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors"
                  >
                    <div className="text-center">
                      {uploading ? (
                        <>
                          <Loader2 className="w-8 h-8 mx-auto mb-2 text-blue-600 animate-spin" />
                          <p className="text-sm text-gray-600">Dosya yükleniyor...</p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-600">
                            Dosya seçmek için tıklayın
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            PDF, DOCX, TXT (Max 15MB)
                          </p>
                        </>
                      )}
                    </div>
                    <input
                      id="file-upload"
                      type="file"
                      className="hidden"
                      accept=".pdf,.docx,.txt"
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                  </label>
                </div>
                {formData.file_name && (
                  <p className="text-sm text-green-600 mt-2">
                    ✓ Yüklendi: {formData.file_name}
                  </p>
                )}
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">veya</span>
                </div>
              </div>

              <div>
                <Label htmlFor="file_url">Dosya URL (Opsiyonel)</Label>
                <div className="flex gap-2">
                  <Input
                    id="file_url"
                    value={formData.file_url}
                    onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                    placeholder="https://example.com/document.pdf"
                  />
                  <Button
                    type="button"
                    onClick={handleFileUrlExtract}
                    disabled={extracting || !formData.file_url}
                  >
                    {extracting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Çıkarılıyor...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        İçeriği Çıkar
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  PDF/DOCX/TXT dosyası URL'si girebilirsiniz
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="content">Doküman İçeriği *</Label>
                  <span className="text-xs text-gray-500">
                    {formData.content.length.toLocaleString()} karakter
                  </span>
                </div>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Doküman içeriğini buraya yapıştırın veya yazın..."
                  rows={20}
                  required
                  className="font-mono text-sm max-h-[500px] overflow-y-auto"
                />
                <p className="text-xs text-gray-500 mt-1">
                  AI chatbot bu içeriği kullanarak teknisyenlere yardım edecek. Çok uzun içerikler otomatik scroll yapabilir.
                </p>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowDialog(false);
                    resetForm();
                  }}
                >
                  İptal
                </Button>
                <Button type="submit">
                  <Plus className="w-4 h-4 mr-2" />
                  Doküman Ekle
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminTechnicalDocs;
